import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OrgDashboardMobile from "./OrgDashboardMobile";
import { UserDashboardSkeleton, MobileDashboardSkeleton } from "../components/SkeletonLoader";
import { useWebSocketEvent } from "../hooks/useWebSocket";
import {
  getAccreditationChecklist,
  getOffcampusChecklist,
  getAccreditationDocuments,
  getOffcampusDocuments,
  getAnnouncements,
  getCurrentAcademicYear,
  getAvatarUrl,
  getAnnouncementImageUrl,
  getMyOrganization,
} from "../services/api";

const OrgDashboard = () => {
  const navigate = useNavigate();

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();
  const isOffCampusUser = user?.application_type === "offcampus";

  const [greeting] = useState(() => {
    const greetings = ["Good day", "Welcome back", "Hello", "Greetings", "Hi there"];
    return greetings[Math.floor(Math.random() * greetings.length)];
  });

  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [organizationId, setOrganizationId] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      return u?.organization_id || null;
    } catch {
      return null;
    }
  });
  const [academicYear, setAcademicYear] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [accChecklist, setAccChecklist] = useState(null);
  const [offChecklist, setOffChecklist] = useState(null);
  const [recentDocs, setRecentDocs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [avatarPath, setAvatarPath] = useState(null);
  const [userId, setUserId] = useState(null);
  const [avatarVersion] = useState(Date.now());

  // Calculates the countdown based on the real academic year lock date
  const calculateTimeLeft = (lockDate) => {
    if (!lockDate) return ["00", "00", "00", "00"];
    const clean = lockDate.replace(/Z|([+-]\d{2}:\d{2})$/, '');
    const [datePart, timePart = "00:00:00"] = clean.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const [h, mi, s = 0] = timePart.split(':').map(Number);
    const lockMs = new Date(y, mo - 1, d, h, mi, s).getTime();
    const difference = lockMs - Date.now();
    if (difference > 0) {
      return [
        Math.floor(difference / (1000 * 60 * 60 * 24))
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / (1000 * 60 * 60)) % 24)
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / 1000 / 60) % 60)
          .toString()
          .padStart(2, "0"),
        Math.floor((difference / 1000) % 60)
          .toString()
          .padStart(2, "0"),
      ];
    }
    return ["00", "00", "00", "00"];
  };

  const [timeLeft, setTimeLeft] = useState(["00", "00", "00", "00"]);

  useEffect(() => {
    if (!academicYear || isOffCampusUser) return;
    setTimeLeft(calculateTimeLeft(academicYear.lock_date));
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(academicYear.lock_date));
    }, 1000);
    return () => clearInterval(timer);
  }, [academicYear, isOffCampusUser]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Pulls the logged-in user's name and organization from localStorage
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      if (u) {
        setUserName(u.full_name ? u.full_name.split(" ")[0] : "there");
        if (u.organization_id) setOrganizationId(u.organization_id);
        if (u.profile_picture_path) setAvatarPath(u.profile_picture_path);
        if (u.user_id) setUserId(u.user_id);
      }
    } catch (e) {
      setUserName("there");
    }
  }, []);

  // Counts how many checklist items have a given status
  const countByStatus = (items, status) =>
    (items || []).filter((i) => i.status === status).length;

  // Returns a small file-type badge based on the file extension
  const getFileBadge = (filePath) => {
    const path = (filePath || "").toLowerCase();
    if (path.endsWith(".pdf"))
      return { label: "PDF", color: "text-red-500 bg-red-50 border-red-100" };
    if (path.endsWith(".doc") || path.endsWith(".docx"))
      return {
        label: "DOC",
        color: "text-blue-500 bg-blue-50 border-blue-100",
      };
    if (
      path.endsWith(".png") ||
      path.endsWith(".jpg") ||
      path.endsWith(".jpeg")
    )
      return {
        label: "IMG",
        color: "text-purple-500 bg-purple-50 border-purple-100",
      };
    return { label: "FILE", color: "text-gray-500 bg-gray-50 border-gray-100" };
  };

  const getStatusBadge = (status) => {
    const map = {
      reviewed: { label: "Reviewed", className: "bg-[#e0f5ee] text-[#10B981]" },
      for_revision: {
        label: "For Revision/s",
        className: "bg-orange-100 text-orange-600",
      },
      rejected: { label: "Rejected", className: "bg-red-100 text-red-600" },
      pending: {
        label: "Pending",
        className: "bg-yellow-100 text-yellow-700",
      },
    };
    return (
      map[status] || { label: status, className: "bg-gray-100 text-gray-600" }
    );
  };

  // Fetches everything this dashboard needs once we know the organization_id
  const fetchDashboardData = async (orgId) => {
    try {
      setLoading(true);

      const [accRes, offRes, docsRes, offDocsRes, annRes, yearRes, orgRes] =
        await Promise.allSettled([
          getAccreditationChecklist(orgId),
          getOffcampusChecklist(orgId),
          getAccreditationDocuments("accreditation", orgId),
          getOffcampusDocuments(orgId),
          getAnnouncements(),
          getCurrentAcademicYear(),
          getMyOrganization(),
        ]);

      if (accRes.status === "fulfilled") setAccChecklist(accRes.value.data);
      if (offRes.status === "fulfilled") setOffChecklist(offRes.value.data);
      if (annRes.status === "fulfilled") setAnnouncements(annRes.value.data);
      if (yearRes.status === "fulfilled") setAcademicYear(yearRes.value.data);
      else setAcademicYear(null);
      if (orgRes.status === "fulfilled") setOrgData(orgRes.value.data);

      const activeDocsRes = isOffCampusUser ? offDocsRes : docsRes;
      if (activeDocsRes.status === "fulfilled") {
        const sorted = [...activeDocsRes.value.data].sort((a, b) => {
          const da = a.submitted_at ? new Date(a.submitted_at) : new Date(0);
          const db = b.submitted_at ? new Date(b.submitted_at) : new Date(0);
          return db - da;
        });
        setRecentDocs(sorted.slice(0, 7));
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchDashboardData(organizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, isOffCampusUser]);

  // Real-time synchronization
  useWebSocketEvent("DOCUMENT_UPDATED", (data) => {
    const currentOrgId = organizationId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.organization_id; } catch { return null; }
    })();
    if (!data?.org_id || String(data.org_id) === String(currentOrgId)) {
      if (currentOrgId) fetchDashboardData(currentOrgId);
    }
  });

  useWebSocketEvent("ORGANIZATION_STATUS_UPDATED", (data) => {
    const currentOrgId = organizationId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.organization_id; } catch { return null; }
    })();
    if (!data?.organization_id || String(data.organization_id) === String(currentOrgId)) {
      if (currentOrgId) fetchDashboardData(currentOrgId);
    }
  });

  useWebSocketEvent("ANNOUNCEMENT_UPDATED", () => {
    const currentOrgId = organizationId || (() => {
      try { return JSON.parse(localStorage.getItem("user"))?.organization_id; } catch { return null; }
    })();
    if (currentOrgId) fetchDashboardData(currentOrgId);
  });

  const activeChecklist = isOffCampusUser ? offChecklist : accChecklist;
  const activeItems = activeChecklist?.items || [];
  const totalSlots = activeItems.length || (isOffCampusUser ? 13 : 10);

  const pendingCount = countByStatus(activeItems, "pending");
  const reviewedCount = countByStatus(activeItems, "reviewed");
  const revisionCount = countByStatus(activeItems, "for_revision");
  const rejectedCount = countByStatus(activeItems, "rejected");

  const calcPercentage = (val) =>
    totalSlots > 0 ? Math.min(100, Math.round((val / totalSlots) * 100)) : 0;

  // Builds the compliance checklist list — submitted items only, sorted by urgency
  const complianceItems = [...activeItems]
    .filter((i) => i.status !== "not_submitted")
    .sort((a, b) => {
      const urgent = ["for_revision", "rejected"];
      const aUrgent = urgent.includes(a.status) ? 0 : 1;
      const bUrgent = urgent.includes(b.status) ? 0 : 1;
      return aUrgent - bUrgent;
    });

  if (loading) {
    if (isMobile) {
      return <MobileDashboardSkeleton />;
    }
    return <UserDashboardSkeleton isOffCampus={isOffCampusUser} />;
  }



  // Build avatar src for mobile header
  const avatarSrc =
    avatarPath && userId
      ? `${getAvatarUrl(userId, avatarPath)}?v=${avatarVersion}`
      : `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=0D3B2E&color=fff`;

  // === MOBILE VIEW ===
  if (isMobile) {
    return (
      <OrgDashboardMobile
        userName={userName}
        isOffCampusUser={isOffCampusUser}
        academicYear={academicYear}
        timeLeft={timeLeft}
        stats={{
          pendingCount,
          reviewedCount,
          revisionCount,
          rejectedCount,
          totalSlots,
        }}
        recentDocs={recentDocs}
        announcements={announcements}
        activeChecklist={activeChecklist}
        complianceItems={complianceItems}
        userAvatar={avatarSrc}
      />
    );
  }

  const primaryTargetUrl = isOffCampusUser ? "/org/off-campus" : "/org/accreditation";
  const portalName = isOffCampusUser ? "Off-Campus Activity" : "Accreditation";

  return (
    <div className="space-y-6">
      {/* Header Area */}
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-[32px] font-extrabold text-gray-900 leading-tight">
            {greeting}, <span className="text-[#086C53]">{userName}!</span>
          </h1>
          <p className="text-[14px] text-gray-500 font-medium mt-1">
            {academicYear
              ? `${academicYear.year_label} · ${portalName} Period`
              : `${portalName} Period`}
          </p>
        </div>
      </div>

      {/* Interactive Deadline Countdown Card — For Accreditation / Re-accreditation Users (Hidden for Off-Campus) */}
      {!isOffCampusUser && academicYear && (
        <div className="bg-sidebar rounded-[10px] p-5 text-white shadow-lg relative overflow-hidden flex flex-col md:flex-row md:items-center w-fit mb-6 pr-12">
          <div className="z-10 mr-12 mb-4 md:mb-0 ml-5">
            <p className="text-[10px] text-gray-300 tracking-widest uppercase font-semibold mb-1">
              Current Cycle Lock Date — {academicYear.year_label}
            </p>
            <h3 className="text-3xl font-bold mb-3 tracking-tight">
              {new Date(academicYear.lock_date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </h3>
            <div
              className={`inline-flex items-center space-x-1.5 text-[11px] px-2.5 py-1 rounded-full border ${new Date(academicYear.lock_date) > new Date()
                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                  : "bg-red-500/20 text-red-400 border-red-500/30"
                }`}
            >

              <span className="font-semibold">
                {new Date(academicYear.lock_date) > new Date()
                  ? "Submissions Open"
                  : "Submissions Closed"}
              </span>
            </div>
          </div>

          <div className="flex space-x-3 z-10">
            {timeLeft.map((val, idx) => {
              const labels = ["DAYS", "HOURS", "MINS", "SECS"];
              return (
                <div key={idx} className="flex flex-col items-center">
                  <div className="bg-[#00A358] rounded-lg w-14 h-16 flex items-center justify-center shadow-inner">
                    <span className="text-2xl font-bold">{val}</span>
                  </div>
                  <span className="text-[9px] text-gray-400 mt-1.5 font-bold tracking-wider">
                    {labels[idx]}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Decorative elements */}
          <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>
          <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-primary/40 rounded-full blur-xl pointer-events-none"></div>
        </div>
      )}

      {/* Stat Cards — Pending / Reviewed / For Revision / Rejected */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Pending */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                Pending
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {pendingCount}
              </h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
                <div
                  className="h-full bg-yellow-400 transition-all"
                  style={{ width: `${calcPercentage(pendingCount)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] font-bold text-gray-500">
                  {pendingCount} of {totalSlots} documents
                </span>
                <span className="text-[10px] font-bold text-gray-700">
                  {calcPercentage(pendingCount)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviewed */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                Reviewed
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {reviewedCount}
              </h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
                <div
                  className="h-full bg-green-500 transition-all"
                  style={{ width: `${calcPercentage(reviewedCount)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] font-bold text-gray-500">
                  {reviewedCount} of {totalSlots} documents
                </span>
                <span className="text-[10px] font-bold text-gray-700">
                  {calcPercentage(reviewedCount)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* For Revision */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                For Revision/s
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {revisionCount}
              </h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
                <div
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${calcPercentage(revisionCount)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] font-bold text-gray-500">
                  {revisionCount} of {totalSlots} documents
                </span>
                <span className="text-[10px] font-bold text-gray-700">
                  {calcPercentage(revisionCount)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Rejected */}
        <div className="bg-white rounded-[16px] p-1 border border-gray-200 shadow-md flex flex-col h-[240px] hover:shadow-md transition-shadow">
          <div className="bg-white rounded-[16px] p-5 border border-gray-200 shadow-sm flex flex-col h-[230px] hover:shadow-md transition-shadow">
            <div>
              <p className="text-[14px] font-bold text-gray-700 leading-tight">
                Rejected
              </p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[72px] font-black text-primary tracking-tighter leading-none mt-1">
                {rejectedCount}
              </h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
                <div
                  className="h-full bg-red-500 transition-all"
                  style={{ width: `${calcPercentage(rejectedCount)}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-[10px] font-bold text-gray-500">
                  {rejectedCount} of {totalSlots} documents
                </span>
                <span className="text-[10px] font-bold text-gray-700">
                  {calcPercentage(rejectedCount)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (Submissions Table) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-[#054334] px-6 py-4 flex justify-between items-center">
              <h2 className="text-white font-bold text-[15px]">
                {isOffCampusUser
                  ? "Recent Off-Campus Activity Submissions"
                  : "Recent Accreditation Submissions"}
              </h2>
              <button
                onClick={() => navigate(primaryTargetUrl)}
                className="text-white text-[12px] font-bold hover:underline flex items-center"
              >
                View All <span className="ml-1">→</span>
              </button>
            </div>

            {/* Table Header */}
            <div className="overflow-x-auto">
              <div className="min-w-[500px]">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                  <div className="col-span-6 text-[12px] font-bold text-gray-500 uppercase tracking-wider">
                    Document
                  </div>
                  <div className="col-span-3 text-[12px] font-bold text-gray-500 uppercase tracking-wider">
                    Date
                  </div>
                  <div className="col-span-3 text-[12px] font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </div>
                </div>

                {/* List — real submitted documents */}
                <div className="divide-y divide-gray-100">
                  {recentDocs.length === 0 ? (
                    <div className="px-6 py-10 text-center text-gray-400 text-sm">
                      No documents submitted yet.
                    </div>
                  ) : (
                    recentDocs.map((doc, idx) => {
                      const badge = getFileBadge(doc.file_path);
                      const status = getStatusBadge(doc.status);
                      const needsAction =
                        doc.status === "for_revision" || doc.status === "rejected";
                      return (
                        <div
                          key={doc.accreditation_document_id || doc.offcampus_document_id || idx}
                          className={`grid grid-cols-12 gap-4 px-6 py-4 items-center ${idx % 2 === 0 ? "bg-[#f8fafc]" : ""}`}
                        >
                          <div className="col-span-6 flex items-center space-x-3">
                            <span
                              className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${badge.color}`}
                            >
                              {badge.label}
                            </span>
                            <span className="text-[13px] font-bold text-gray-600">
                              {doc.document_name}
                            </span>
                          </div>
                          <div className="col-span-3 text-[13px] text-gray-600 font-medium">
                            {doc.submitted_at
                              ? new Date(doc.submitted_at).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" },
                              )
                              : "—"}
                          </div>
                          <div className="col-span-3 flex justify-between items-center">
                            <span
                              className={`${status.className} px-2 py-0.5 rounded-md text-[11px] font-bold whitespace-nowrap`}
                            >
                              {status.label}
                            </span>
                            <button
                              onClick={() => navigate(primaryTargetUrl)}
                              className="px-3 py-1 border border-gray-300 rounded text-[11px] font-bold text-gray-600 hover:bg-gray-50 transition"
                            >
                              {needsAction ? "Resubmit" : "View"}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (Widgets) */}
        <div className="space-y-6">
          {/* Compliance Progress Widget */}
          <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden flex flex-col">
            <div className="bg-[#054334] px-5 py-3 flex justify-between items-center">
              <h2 className="text-white font-bold text-[14px]">
                Compliance Progress
              </h2>
              <span className="text-white font-bold text-[14px]">
                {activeChecklist
                  ? `${activeChecklist.submitted}/${activeChecklist.total_required}`
                  : "0/0"}
              </span>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <div className="flex justify-between items-end mb-2">
                <span className="text-[12px] font-bold text-gray-500">
                  Overall Completion
                </span>
                <span className="text-[12px] font-bold text-gray-800">
                  {activeChecklist
                    ? Math.round(activeChecklist.progress_percentage)
                    : 0}
                  %
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-5">
                <div
                  className="h-full bg-[#10B981] rounded-full"
                  style={{
                    width: `${activeChecklist ? activeChecklist.progress_percentage : 0}%`,
                  }}
                ></div>
              </div>

              <ul className="space-y-3 flex-1 overflow-y-auto mb-5 max-h-[220px]">
                {complianceItems.length === 0 ? (
                  <li className="text-[12px] text-gray-400">
                    No documents submitted yet.
                  </li>
                ) : (
                  complianceItems.map((item) => {
                    const needsAction = ["for_revision", "rejected"].includes(
                      item.status,
                    );
                    if (item.status === "reviewed") {
                      return (
                        <li
                          key={item.document_name}
                          className="flex items-center space-x-3"
                        >
                          <svg
                            className="w-4 h-4 text-[#10B981] flex-shrink-0 bg-[#e0f5ee] rounded-full p-0.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-[12px] text-gray-400 font-medium line-through">
                            {item.document_name}
                          </span>
                        </li>
                      );
                    }
                    if (needsAction) {
                      return (
                        <li
                          key={item.document_name}
                          className="flex items-center space-x-3"
                        >
                          <span className="w-4 h-4 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center font-black text-[10px] flex-shrink-0">
                            !
                          </span>
                          <span className="text-[12px] text-yellow-600 font-bold">
                            {item.document_name}
                          </span>
                        </li>
                      );
                    }
                    // Pending review — submitted but not yet acted on by admin
                    return (
                      <li
                        key={item.document_name}
                        className="flex items-center space-x-3"
                      >
                        <span className="w-4 h-4 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center font-black text-[10px] flex-shrink-0">
                          •
                        </span>
                        <span className="text-[12px] text-gray-500 font-medium">
                          {item.document_name}
                        </span>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div className="p-4 pt-0">
              <button
                onClick={() => navigate(primaryTargetUrl)}
                className="w-full py-2.5 rounded-lg text-[13px] font-bold text-white transition-colors"
                style={{ background: "#054334" }}
              >
                View Documents
              </button>
            </div>
          </div>

          {/* Announcements — real data from the backend */}
          <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 p-5 relative">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-2">
              <h2 className="font-bold text-[14px] text-gray-800">
                Announcements
              </h2>
              <button
                onClick={() => navigate("/org/announcements")}
                className="text-[#086C53] text-[11px] font-bold hover:underline flex items-center"
              >
                View All <span className="ml-1">→</span>
              </button>
            </div>

            {announcements.length === 0 ? (
              <p className="text-[13px] text-gray-400">No announcements yet.</p>
            ) : (
              <div className="space-y-2">
                <span className="bg-[#e0f5ee] text-[#10B981] px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider inline-block">
                  {announcements[0].title}
                </span>
                {announcements[0].image_path && (
                  <img
                    src={getAnnouncementImageUrl(announcements[0])}
                    alt={announcements[0].title}
                    className="w-full h-28 object-cover rounded-lg border border-gray-100"
                  />
                )}
                <p className="text-[13px] text-gray-800 font-bold leading-snug">
                  {announcements[0].content.length > 100
                    ? announcements[0].content.slice(0, 100) + "..."
                    : announcements[0].content}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgDashboard;
